import { useCallback, useEffect, useState } from 'react';
import api from '../config/api';
import { getSessionToken } from './authSession';

// One shared copy of the signed-in user's favourite restaurant and dish ids, so every
// heart on the page (listings, restaurant menu, search) shows the same state.
const store = { loading: null, restaurant: new Set(), product: new Set() };
const listeners = new Set();
const notify = () => listeners.forEach((listener) => listener());

const ENDPOINTS = { restaurant: '/user/favorites/restaurants', product: '/user/favorites/products' };

const loadFavorites = () => {
  if (store.loading || !getSessionToken()) return store.loading;
  store.loading = Promise.all(Object.entries(ENDPOINTS).map(([kind, url]) =>
    api.get(url).then((res) => {
      store[kind] = new Set((res.data?.favorites || []).map((item) => String(item._id || item.id)));
    })
  ))
    .then(notify)
    .catch(() => { store.loading = null; });
  return store.loading;
};

/**
 * [isFavorite, toggle] for one restaurant ("restaurant") or dish ("product").
 * toggle() returns false when the user must sign in first.
 */
export const useFavorite = (kind, id) => {
  const key = id ? String(id) : '';
  const [isFavorite, setIsFavorite] = useState(() => store[kind].has(key));

  useEffect(() => {
    const sync = () => setIsFavorite(store[kind].has(key));
    listeners.add(sync);
    sync();
    loadFavorites();
    return () => listeners.delete(sync);
  }, [kind, key]);

  const toggle = useCallback(async () => {
    if (!key) return true;
    if (!getSessionToken()) return false;
    const set = store[kind];
    const wasFavorite = set.has(key);
    wasFavorite ? set.delete(key) : set.add(key);
    notify();
    try {
      const res = await api.post(`${ENDPOINTS[kind]}/${key}`);
      if (typeof res.data?.isFavorite === 'boolean' && res.data.isFavorite === wasFavorite) {
        res.data.isFavorite ? set.add(key) : set.delete(key);
        notify();
      }
    } catch (err) {
      wasFavorite ? set.add(key) : set.delete(key);
      notify();
    }
    return true;
  }, [kind, key]);

  return [isFavorite, toggle];
};
